import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  Users, 
  TrendingUp, 
  ChevronDown, 
  ChevronUp, 
  Copy, 
  Share2,
  ArrowUpDown,
  Gift,
  HelpCircle,
  Gem,
  Trophy,
  ChevronRight
} from 'lucide-react'
import useAppStore from '../store/useAppStore'
import useAppEnvironment from '../hooks/useAppEnvironment'
import { userApi } from '../services'
import toast from 'react-hot-toast'
import HelpModal from '../components/HelpModal'

const Referrals = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user, shareInviteMessage } = useAppStore()
  const { isWebBrowser } = useAppEnvironment()
  
  // Состояния
  const [isLoading, setIsLoading] = useState(true)
  const [referralData, setReferralData] = useState(null)
  const [selectedPeriod, setSelectedPeriod] = useState('lifetime') // today, week, month, lifetime
  const [expandedReferrals, setExpandedReferrals] = useState(new Set())
  const [sortBy, setSortBy] = useState('sum') // sum, subrefs
  const [showHelp, setShowHelp] = useState(false)
  const [displayCount, setDisplayCount] = useState(10) // Количество отображаемых рефералов
  const [avatarErrorIds, setAvatarErrorIds] = useState(() => new Set())

  // Загрузка данных при монтировании
  useEffect(() => {
    loadReferralStats()
  }, [])

  // Функция загрузки статистики рефералов
  const loadReferralStats = async () => {
    try {
      setIsLoading(true)
      const response = await userApi.getReferralStats()
      setReferralData(response)
    } catch (error) {
      console.error('Ошибка загрузки статистики рефералов:', error)
      toast.error(t('referrals.loadError'))
    } finally {
      setIsLoading(false)
    }
  }

  // Переключение развернутого состояния реферала
  const toggleReferral = (uniqueId) => {
    const newExpanded = new Set(expandedReferrals)
    if (newExpanded.has(uniqueId)) {
      newExpanded.delete(uniqueId)
    } else {
      newExpanded.add(uniqueId)
    }
    setExpandedReferrals(newExpanded)
  }

  // Копирование реферальной ссылки
  const handleCopyLink = async () => {
    const referralLink = isWebBrowser ? user?.ref_link_website : user?.ref_link

    if (!referralLink) {
      toast.error(t('referrals.linkNotFound'))
      return
    }

    try {
      await navigator.clipboard.writeText(referralLink)
      toast.success(t('referrals.linkCopied'))
    } catch (error) {
      console.error('Ошибка копирования:', error)
      toast.error(t('referrals.copyError'))
    }
  }

  // Поделиться реферальной ссылкой
  const handleShareLink = async () => {
    if (isWebBrowser || !user?.ref_link) {
      toast.error(t('referrals.linkNotFound'))
      return
    }

    const inviteText = t('invite.message')

    try {
      await shareInviteMessage(inviteText, user.ref_link, {
        shareTitle: t('invite.shareTitle'),
        shareMessage: t('invite.shareMessage'),
        copy: t('invite.copy'),
        close: t('common.close'),
        copySuccess: t('invite.copySuccess'),
        copySuccessBrowser: t('invite.copySuccessBrowser'),
        copyManual: t('invite.copyManual'),
        createMessageError: t('invite.createMessageError')
      })
    } catch (error) {
      console.error('Ошибка отправки приглашения:', error)
    }
  }

  // Сортировка рефералов
  const getSortedReferrals = () => {
    if (!referralData?.ref) return []
    
    const refs = [...referralData.ref]
    
    if (sortBy === 'sum') {
      // Сортировка по общему доходу за выбранный период
      return refs.sort((a, b) => {
        const periodKey = `sum_${selectedPeriod}`
        return (b.stat[periodKey] || 0) - (a.stat[periodKey] || 0)
      })
    } else if (sortBy === 'subrefs') {
      // Сортировка по количеству сабрефералов
      return refs.sort((a, b) => (b.subref_count || 0) - (a.subref_count || 0))
    }
    
    return refs
  }

  // Получение данных для выбранного периода
  const getPeriodData = () => {
    if (!referralData?.stat) return null
    
    return {
      level1: referralData.stat[`level_1_${selectedPeriod}`] || 0,
      level2: referralData.stat[`level_2_${selectedPeriod}`] || 0,
      sum: referralData.stat[`sum_${selectedPeriod}`] || 0
    }
  }

  // Форматирование числа с пробелами
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  }

  // Функция для загрузки еще рефералов
  const loadMoreReferrals = () => {
    setDisplayCount(prev => prev + 10)
  }

  const markAvatarError = (uniqueId) => {
    setAvatarErrorIds(prev => {
      if (prev.has(uniqueId)) return prev
      const next = new Set(prev)
      next.add(uniqueId)
      return next
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-primary text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-accent-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        </div>
      </div>
    )
  }

  const periodData = getPeriodData()
  const sortedReferrals = getSortedReferrals()
  const displayedReferralLink = isWebBrowser ? user?.ref_link_website : user?.ref_link

  return (
    <div className="min-h-screen bg-dark-primary text-white pb-4 lg:max-w-2xl lg:mx-auto">
      {/* Header */}
      <div className="p-6 border-b border-white border-opacity-10 text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Users size={36} className="text-dark-primary" />
        </div>
        <div className="flex items-center gap-2 justify-center mb-2">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-accent-primary to-accent-secondary bg-clip-text text-transparent">
            {t('referrals.title')}
          </h1>
          <button
            onClick={() => setShowHelp(true)}
            className="p-1.5 rounded-full bg-dark-secondary/50 hover:bg-dark-secondary transition-colors"
          >
            <HelpCircle className="w-5 h-5 text-text-secondary" />
          </button>
        </div>
        <p className="text-text-secondary text-sm">
          {t('referrals.subtitle')}
        </p>
        <p className="text-text-secondary text-sm">
          {t('referrals.earnUpTo')}
        </p>
      </div>

      {/* Реферальная ссылка */}
      <div className="p-6">
        <div className="bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 rounded-lg p-4 border border-accent-primary/30">
          <div className="flex items-center gap-2 mb-3">
            <Gift className="text-accent-primary" size={20} />
            <h3 className="font-bold text-white">{t('referrals.yourLink')}</h3>
          </div>
          
          <textarea
            readOnly
            value={displayedReferralLink || t('common.loading')}
            rows={1}
            className="w-full bg-dark-primary rounded-lg p-3 mb-3 text-sm text-text-secondary resize-none overflow-x-auto whitespace-nowrap border border-white/10 focus:border-accent-primary focus:outline-none cursor-text"
            onClick={(e) => e.target.select()}
          />

          <div className="flex gap-2">
            <button
              onClick={handleCopyLink}
              className={`${isWebBrowser ? 'w-full' : 'w-12'} h-12 bg-surface text-white rounded-lg hover:bg-accent-primary/10 transition-all flex items-center justify-center`}
              title={t('referrals.copy')}
            >
              <Copy size={20} />
            </button>
            {!isWebBrowser && (
              <button
                onClick={handleShareLink}
                className="flex-1 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary py-3 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                title={t('referrals.share')}
              >
                <Share2 size={20} />
                {t('referrals.share')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Общая статистика */}
      <div className="px-6 pb-6">
        <h3 className="font-bold text-white flex items-center gap-2 text-base mb-4">
            <TrendingUp size={18} className="text-accent-primary" />
            {t('referrals.totalEarnings')}
        </h3>
        <div className="grid grid-cols-3 gap-3">
          {/* Всего рефералов */}
          <div className="bg-surface rounded-lg p-4 border border-white border-opacity-10">
            <div className="flex items-center gap-2 mb-2">
              <Users className="text-accent-primary" size={16} />
            </div>
            <div className="text-2xl font-bold text-white mb-1">
              {referralData?.all_ref_count || 0}
            </div>
            <div className="text-xs text-text-secondary">{t('referrals.totalReferrals')}</div>
          </div>

          {/* Уровень 1 */}
          <div className="bg-surface rounded-lg p-4 border border-white border-opacity-10">
            <div className="flex items-center gap-2 mb-2">
              <Users className="text-blue-400" size={16} />
            </div>
            <div className="text-2xl font-bold text-white mb-1">
              {referralData?.level_1_count || 0}
            </div>
            <div className="text-xs text-text-secondary">{t('referrals.level1')}</div>
          </div>

          {/* Уровень 2 */}
          <div className="bg-surface rounded-lg p-4 border border-white border-opacity-10">
            <div className="flex items-center gap-2 mb-2">
              <Users className="text-purple-400" size={16} />
            </div>
            <div className="text-2xl font-bold text-white mb-1">
              {referralData?.level_2_count || 0}
            </div>
            <div className="text-xs text-text-secondary">{t('referrals.level2')}</div>
          </div>
        </div>
      </div>

      {/* Ссылка на ТОП партнеров */}
      <div className="px-6 pb-6">
        <button
          onClick={() => navigate('/referrals/top')}
          className="w-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-lg p-4 hover:scale-[1.02] transition-all"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-yellow-500/20 rounded-full">
                <Trophy className="text-yellow-500" size={24} />
              </div>
              <div className="text-left">
                <p className="font-bold text-white text-base">{t('referrals.topPartners')}</p>
              </div>
            </div>
            <ChevronRight className="text-text-secondary" size={20} />
          </div>
        </button>
      </div>

      {/* Табы периодов */}
      <div className="px-6 pb-4">
        <div className="grid grid-cols-4 gap-2">
          {[
            { key: 'today', label: t('referrals.periodDay') },
            { key: 'week', label: t('referrals.periodWeek') },
            { key: 'month', label: t('referrals.periodMonth') },
            { key: 'lifetime', label: t('referrals.periodLifetime') }
          ].map(period => (
            <button
              key={period.key}
              onClick={() => setSelectedPeriod(period.key)}
              className={`px-3 py-2.5 rounded-lg font-medium transition-all text-sm ${
                selectedPeriod === period.key
                  ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary'
                  : 'bg-surface text-text-secondary hover:text-white border border-white border-opacity-10'
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {/* Статистика доходов за период */}
      {periodData && (
        <div className="px-6 pb-6">
          <div className="bg-gradient-to-br from-accent-primary/10 to-accent-secondary/10 rounded-lg p-4 border border-accent-primary/30">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="text-accent-primary" size={20} />
              <h3 className="font-bold text-white">{t('referrals.earnings')}</h3>
            </div>

            <div className="space-y-3">
              {/* 1 уровень */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">{t('referrals.level1')}</span>
                <span className="text-base font-bold text-blue-400 flex items-center gap-1">
                  <Gem size={16} /> {formatNumber(periodData.level1)}
                </span>
              </div>

              {/* 2 уровень */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">{t('referrals.level2')}</span>
                <span className="text-base font-bold text-purple-400 flex items-center gap-1">
                  <Gem size={16} /> {formatNumber(periodData.level2)}
                </span>
              </div>

              {/* Всего */}
              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                <span className="text-sm text-text-secondary">{t('referrals.periodLifetime')}</span>
                <span className="text-lg font-bold text-accent-primary flex items-center gap-1">
                  <Gem size={18} /> {formatNumber(periodData.sum)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Список рефералов */}
      <div className="px-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-white flex items-center gap-2 text-base">
            <Users size={18} className="text-accent-primary" />
            {t('referrals.referralsList')}
          </h3>

          {/* Сортировка */}
          {/* {sortedReferrals.length > 0 && (
            <div className="flex gap-2">
              <button
                onClick={() => setSortBy('sum')}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                  sortBy === 'sum'
                    ? 'bg-accent-primary text-dark-primary'
                    : 'bg-surface text-text-secondary hover:text-white border border-white border-opacity-10'
                }`}
              >
                <TrendingUp size={12} />
                Доход
              </button>
              <button
                onClick={() => setSortBy('subrefs')}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                  sortBy === 'subrefs'
                    ? 'bg-accent-primary text-dark-primary'
                    : 'bg-surface text-text-secondary hover:text-white border border-white border-opacity-10'
                }`}
              >
                <Users size={12} />
                Рефы
              </button>
            </div>
          )} */}
        </div>

        {/* Пустое состояние */}
        {sortedReferrals.length === 0 ? (
          <div className="bg-surface rounded-lg p-8 border border-white border-opacity-10 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-accent-primary/20 to-accent-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Gift className="text-accent-primary" size={40} />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              {t('referrals.noReferrals')}
            </h3>
            <p className="text-text-secondary mb-6">
              {t('referrals.inviteFriends')}
            </p>
            <button
              onClick={handleShareLink}
              className="bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold py-3 px-6 rounded-lg hover:opacity-90 transition-opacity inline-flex items-center gap-2"
            >
              <Share2 size={18} />
              {t('referrals.shareLink')}
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {sortedReferrals.slice(0, displayCount).map((referral, index) => {
              // Используем chat_id или индекс как уникальный идентификатор
              const uniqueId = referral.chat_id ? String(referral.chat_id) : `ref-${index}`
              const isExpanded = expandedReferrals.has(uniqueId)
              const periodKey = `sum_${selectedPeriod}`
              const periodIncome = referral.stat[periodKey] || 0
              const shouldShowAvatar = Boolean(referral.user_info?.ava) && !avatarErrorIds.has(uniqueId)

              return (
                <div
                  key={uniqueId}
                  className="bg-surface rounded-lg border border-white border-opacity-10 overflow-hidden transition-all"
                >
                  {/* Свернутая карточка */}
                  <button
                    onClick={() => toggleReferral(uniqueId)}
                    className="w-full p-4 flex items-center gap-3 hover:bg-white/5 transition-all"
                  >
                    {/* Аватар */}
                    {shouldShowAvatar ? (
                      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-accent-primary/30 flex-shrink-0">
                        <img
                          src={referral.user_info.ava}
                          alt={referral.user_info.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // При ошибке загрузки показываем fallback
                            markAvatarError(uniqueId)
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-accent-primary/30 flex-shrink-0">
                        <div className="w-full h-full bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center">
                          <Users size={24} className="text-dark-primary" />
                        </div>
                      </div>
                    )}

                    {/* Информация */}
                    <div className="flex-1 text-left min-w-0">
                      <div className="font-bold text-white mb-1 truncate">
                        {referral.user_info?.name || t('common.loading')}
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-text-secondary flex items-center gap-1">
                          <Users size={12} className="text-purple-400" />
                          {referral.subref_count}
                        </span>
                        <span className="text-accent-primary font-bold flex items-center gap-1">
                          <Gem size={12} /> {formatNumber(periodIncome)}
                        </span>
                      </div>
                    </div>

                    {/* Иконка раскрытия */}
                    <div className="flex-shrink-0">
                      {isExpanded ? (
                        <ChevronUp className="text-accent-primary" size={20} />
                      ) : (
                        <ChevronDown className="text-text-secondary" size={20} />
                      )}
                    </div>
                  </button>

                  {/* Развернутая статистика */}
                  {isExpanded && (
                    <div className="border-t border-white border-opacity-10 p-4 bg-dark-primary/50">
                      <div className="grid grid-cols-2 gap-3">
                        {/* Доход от реферала */}
                        <div className="bg-surface rounded-lg p-3 border border-blue-500/30">
                          <div className="text-[10px] text-text-secondary mb-2 flex items-center gap-1">
                            <Users size={10} className="text-blue-400" />
                            {t('referrals.fromReferral')}
                          </div>
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="text-text-secondary">{t('referrals.periodDay')}:</span>
                              <span className="text-white font-medium flex items-center gap-1">
                                <Gem size={10} /> {formatNumber(referral.stat.today || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-text-secondary">{t('referrals.periodWeek')}:</span>
                              <span className="text-white font-medium flex items-center gap-1">
                                <Gem size={10} /> {formatNumber(referral.stat.week || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-text-secondary">{t('referrals.periodMonth')}:</span>
                              <span className="text-white font-medium flex items-center gap-1">
                                <Gem size={10} /> {formatNumber(referral.stat.month || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs border-t border-white/10 pt-1 mt-1">
                              <span className="text-text-secondary">{t('referrals.periodLifetime')}:</span>
                              <span className="text-blue-400 font-bold flex items-center gap-1">
                                <Gem size={10} /> {formatNumber(referral.stat.lifetime || 0)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Доход от сабрефералов */}
                        <div className="bg-surface rounded-lg p-3 border border-purple-500/30">
                          <div className="text-[10px] text-text-secondary mb-2 flex items-center gap-1">
                            <Users size={10} className="text-purple-400" />
                            {t('referrals.fromSubreferrals')}
                          </div>
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="text-text-secondary">{t('referrals.periodDay')}:</span>
                              <span className="text-white font-medium flex items-center gap-1">
                                <Gem size={10} /> {formatNumber(referral.stat.subref_today || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-text-secondary">{t('referrals.periodWeek')}:</span>
                              <span className="text-white font-medium flex items-center gap-1">
                                <Gem size={10} /> {formatNumber(referral.stat.subref_week || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-text-secondary">{t('referrals.periodMonth')}:</span>
                              <span className="text-white font-medium flex items-center gap-1">
                                <Gem size={10} /> {formatNumber(referral.stat.subref_month || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs border-t border-white/10 pt-1 mt-1">
                              <span className="text-text-secondary">{t('referrals.periodLifetime')}:</span>
                              <span className="text-purple-400 font-bold flex items-center gap-1">
                                <Gem size={10} /> {formatNumber(referral.stat.subref_lifetime || 0)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Общий доход */}
                        <div className="col-span-2 bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 rounded-lg p-3 border border-accent-primary/30">
                          <div className="text-xs text-text-secondary mb-3 flex items-center gap-1">
                            <TrendingUp size={12} className="text-accent-primary" />
                            {t('referrals.totalIncome')}
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-text-secondary">{t('referrals.periodDay')}</span>
                              <span className="text-xs font-bold text-white flex items-center gap-1">
                                <Gem size={10} /> {formatNumber(referral.stat.sum_today || 0)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-text-secondary">{t('referrals.periodWeek')}</span>
                              <span className="text-xs font-bold text-white flex items-center gap-1">
                                <Gem size={10} /> {formatNumber(referral.stat.sum_week || 0)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-text-secondary">{t('referrals.periodMonth')}</span>
                              <span className="text-xs font-bold text-white flex items-center gap-1">
                                <Gem size={10} /> {formatNumber(referral.stat.sum_month || 0)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-text-secondary">{t('referrals.periodLifetime')}</span>
                              <span className="text-xs font-bold text-accent-primary flex items-center gap-1">
                                <Gem size={10} /> {formatNumber(referral.stat.sum_lifetime || 0)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Кнопка "Показать еще" */}
          {displayCount < sortedReferrals.length && (
            <button
              onClick={loadMoreReferrals}
              className="w-full mt-4 py-3 px-4 rounded-lg bg-surface text-white hover:bg-accent-primary/10 transition-all border border-white/10"
            >
              {t('referrals.showMore', { count: Math.min(10, sortedReferrals.length - displayCount) })}
            </button>
          )}
          </>
        )}
      </div>

      {/* Модальное окно с условиями */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title={t('referrals.helpTitle')}
        steps={[
          t('referrals.helpStep1'),
          t('referrals.helpStep2'),
          t('referrals.helpStep3'),
          t('referrals.helpStep4')
        ]}
        highlightLastStep={true}
        buttonText={t('referrals.helpGotIt')}
      />
    </div>
  )
}

export default Referrals
